// Angular
import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

// Libraries
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

// Services
import { CoreService } from '../../../../shared/services';
import { OutputConfigService } from '../../services/output-config.service';

// Models
import { ChannelViewModel, OutputChannelViewModel } from '../../../unit-config/models/unit-config-view.model';
import { OutputConfigViewModel } from '../../models/output-config-view.model';

// Helpers
import { FormHelper } from '../../../../shared/helpers';

// Components
import { AppCleanup } from '../../../../shared/models';

// Enums
import { StatusType } from '../../../../shared/enums';

@Component({
	selector: 'output-config-channel-add-dialog',
	templateUrl: './output-config-channel-add-dialog.component.html',
	styleUrls: ['./output-config-channel-add-dialog.component.scss'],
})
export class OutputConfigChannelAddDialogComponent extends AppCleanup implements OnInit, OnDestroy {
	outputConfig: OutputConfigViewModel;
	channel: ChannelViewModel;
	form: FormGroup;
	isEdit: boolean = false;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: { outputConfig: OutputConfigViewModel; channel: ChannelViewModel; channels: ChannelViewModel[] },
		private dialogRef: MatDialogRef<OutputConfigChannelAddDialogComponent>,
		private formBuilder: FormBuilder,
		private outputConfigService: OutputConfigService,
		private coreService: CoreService,
		public dialog: MatDialog,
		private translateService: TranslateService
	) {
		super();
	}

	ngOnInit() {
		if (this.data?.outputConfig) {
			this.outputConfig = this.data.outputConfig;
		}

		if (this.data?.channel) {
			// If edit
			this.isEdit = true;
			this.channel = this.data.channel;
		}

		this.form = this.formBuilder.group({
			name: [this.isEdit ? this.channel.name : null, [Validators.required, Validators.maxLength(30)]],
			enabled: [this.isEdit ? this.channel.enabled : true],
		});
	}

	ngOnDestroy() {
		super.ngOnDestroy();
	}

	controlHasError(controlName: string, validationType: string): boolean {
		return FormHelper.controlHasError(this.form, controlName, validationType);
	}

	submit() {
		if (!this.form.dirty) return;

		const controls = this.form.controls;

		if (this.form.invalid) {
			Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
			return;
		}

		this.form.markAsPristine();
		this.form.markAsUntouched();
		this.form.updateValueAndValidity();

		let newChannel = new OutputChannelViewModel();
		let message: string = '';
		newChannel.name = controls.name?.value;
		newChannel.enabled = controls.enabled?.value;

		if (this.data.channel) {
			let index = this.outputConfig.channels.findIndex((channel) => channel.name === this.channel?.name);
			this.outputConfig.channels[index] = newChannel;
		} else {
			this.outputConfig.channels.push(newChannel);
		}

		this.outputConfigService
			.updateOutputConfig(this.outputConfig)
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (c) => {
					this.dialogRef.close(this.outputConfig);
					message = `${this.data.channel ? this.translateService.instant('GENERIC.UPDATED', { name: this.translateService.instant('MODULE.CHANNEL.TITLE_CAPITAL') }) : this.translateService.instant('GENERIC.CREATED', { name: this.translateService.instant('MODULE.CHANNEL.TITLE_CAPITAL') })}`;
					this.coreService.toast({ message: message, type: StatusType.SUCCESS });
				},
				error: () => {
					if (!this.data.channel) {
						this.outputConfig.channels.pop();
					}
					message = `${this.data.channel ? this.translateService.instant('GENERIC.NOT_UPDATED', { name: this.translateService.instant('MODULE.CHANNEL.TITLE_CAPITAL') }) : this.translateService.instant('GENERIC.NOT_CREATED', { name: this.translateService.instant('MODULE.CHANNEL.TITLE_CAPITAL') })}`;
					this.coreService.toast({ message: message, type: StatusType.DANGER });
				},
			});
	}
}
